// Dependencies
import { useSnackbar } from "notistack";
import { FC, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";

// Components
import { If } from "jsx-control-statements";
import SearchBar from "~/components/Common/SearchBar";
import WalletInfo from "~/components/Wallets/WalletInfo";
import WalletList from "~/containers/Wallets/WalletList";
import WalletEdit from "~/components/Wallets/WalletEdit";
import LayoutDelete from "~/components/Layout/WalletDelete";
import WalletAddMember from "~/components/Wallets/WalletAddMember";
import CategoryWrapper from "~/containers/Category/CategoryWrapper";

// types
import { QUERY_KEY } from "~/enums";
import { TWalletDetailData, TWalletEditData, TQueryError } from "~/types";

// apis
import { deleteWallet, editWallet, getWalletInfo } from "~/services/wallet";

// utils
import { getChangedValues } from "~/utils";

// Styles
import { StyledContentWrapper } from "./Wallets.styled";
import { StyledModal } from "~/components/Layout/LayoutInfo";
import { StyledLayoutContent, StyledLayoutWrapper } from "~/components/Layout";

interface WalletsProps {}

const Wallets: FC<WalletsProps> = () => {
  // States
  const [walletId, setWalletId] = useState<string>("");
  const [showDetail, setShowDetail] = useState<boolean>(false);
  const [showEdit, setShowEdit] = useState<boolean>(false);
  const [showDelete, setShowDelete] = useState<boolean>(false);
  const [showAddMember, setShowAddMember] = useState<boolean>(false);

  const [showCategories, setShowCategories] = useState<boolean>(false);

  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  /**close Edit form when user choose another wallet */
  useEffect(() => {
    setShowEdit(false);
  }, [walletId]);

  // queries
  const walletQuery = useQuery({
    queryKey: [QUERY_KEY.WALLET.WALLET_INFO, walletId],
    queryFn: () => getWalletInfo(walletId),
    enabled: !!walletId,
    onError(error: TQueryError) {},
  });

  // mutations
  const editWalletMutation = useMutation({
    mutationFn: editWallet,
    onSuccess() {
      enqueueSnackbar("Edit Wallet success!", { variant: "success" });
      queryClient.invalidateQueries(QUERY_KEY.WALLET.WALLET_INFO);
      queryClient.invalidateQueries(QUERY_KEY.WALLET.WALLET_LIST);

      /** close edit when saving successful */
      handleCancelEdit();
    },
    onError(error: TQueryError) {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  const deleteWalletMutation = useMutation({
    mutationFn: deleteWallet,
    onSuccess() {
      enqueueSnackbar("Delete Wallet success!", { variant: "success" });
      queryClient.invalidateQueries(QUERY_KEY.WALLET.WALLET_LIST);
      setShowDelete(false);
      setShowDetail(false);
      setWalletId("");
    },
    onError(error: TQueryError) {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  const walletData: TWalletDetailData | undefined = walletQuery.data;

  const userInfo = JSON.parse(localStorage.getItem("user-info") || "");
  const username = userInfo?.name;
  // get member list
  const members = walletData?.users?.filter((member) => member !== username);

  // Methods
  /** Show Wallet Detail */
  const handleShowDetail = (walletId: string) => {
    setShowEdit(false);
    setShowDetail(true);
    setWalletId(walletId);
  };
  /************************************ */

  /** Edit Wallet */
  const handleShowEdit = () => {
    setShowEdit(true);
    setShowDetail(false);
  };

  const handleSaveEdit = (data: TWalletEditData) => {
    if (walletData?.id) {
      // check changed value
      const editedData = getChangedValues(walletData, data);
      editWalletMutation.mutate({ ...editedData, id: walletData?.id });
    }
  };

  const handleCancelEdit = () => {
    setShowEdit(false);
    setShowDetail(true);
  };
  /************************************* */

  /** Delete Wallet */
  const handleShowDelete = () => {
    setShowDelete(true);
  };

  const handleCloseDelete = () => {
    setShowDelete(false);
  };

  const handleDelete = () => {
    deleteWalletMutation.mutate(walletId);
  };
  /************************************ */

  /** Add Member */
  const handleAddMember = () => {
    setShowAddMember(true);
  };

  const handleCloseAddMember = () => {
    setShowAddMember(false);
  };
  /****************************************/

  /** Show category */
  const handleShowCategory = () => {
    setShowCategories(true);
  };

  const handleCloseCategory = () => {
    setShowCategories(false);
  };

  return (
    <StyledLayoutWrapper>
      <SearchBar />
      <StyledContentWrapper>
        <If condition={!showCategories}>
          <StyledLayoutContent>
            <WalletList onShowDetail={handleShowDetail} />
            <If condition={showDetail}>
              <WalletInfo
                data={walletData}
                error={walletQuery?.error?.message}
                status={{ ...walletQuery }}
                onEdit={handleShowEdit}
                onDelete={handleShowDelete}
                onAddMember={handleAddMember}
                onShowCategory={handleShowCategory}
              />
            </If>
            <If condition={showEdit}>
              <WalletEdit
                onSubmit={handleSaveEdit}
                onCancel={handleCancelEdit}
                initialData={walletData}
              />
            </If>
          </StyledLayoutContent>
        </If>
        <If condition={showCategories}>
          <CategoryWrapper walletId={walletId} onClose={handleCloseCategory} />
        </If>
      </StyledContentWrapper>
      <StyledModal open={showDelete}>
        <div>
          <LayoutDelete
            title={"Are you sure to delete this wallet?"}
            name={walletData?.name}
            onCancel={handleCloseDelete}
            onConfirm={handleDelete}
          />
        </div>
      </StyledModal>
      <StyledModal open={showAddMember} onClose={handleCloseAddMember}>
        <div>
          <WalletAddMember
            walletId={walletId}
            members={members}
            onClose={handleCloseAddMember}
          />
        </div>
      </StyledModal>
    </StyledLayoutWrapper>
  );
};

export default Wallets;
